import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  createDispensation,
  getDispensationById,
  getDispensationPdfBlob,
  getDispensations,
  openDispensationDownload,
  updateDispensation,
  type CreateDispensationParams,
  type DispensationEvidenceListModel,
  type DispensationEvidenceModel,
  type PaginatedResponse,
  type UpdateDispensationParams,
} from "@/api/cle-api";

import {
  mergeEvidenceDetailUpdate,
  reviewThreadAuditTrailQueryKey,
} from "./useReviewThread";

export type { UpdateDispensationParams } from "@/api/cle-api";

export interface UseDispensationsParams {
  userId?: string;
  page?: number;
  pageSize?: number;
  reported?: boolean;
}

export interface UseDispensationParams {
  userId?: string;
  dispensationId?: string;
}

export interface DownloadDispensationParams {
  userId: string;
  dispensationId: string;
}

export const dispensationsQueryKey = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: UseDispensationsParams) =>
  [
    "cle-api",
    "users",
    userId,
    "dispensations",
    page,
    pageSize,
    reported,
  ] as const;

export const dispensationQueryKey = ({
  userId,
  dispensationId,
}: UseDispensationParams) =>
  ["cle-api", "users", userId, "dispensations", dispensationId] as const;

export const dispensationPdfQueryKey = ({
  userId,
  dispensationId,
}: UseDispensationParams) =>
  ["cle-api", "users", userId, "pdf", "dispensations", dispensationId] as const;

export const useDispensations = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: UseDispensationsParams): UseQueryResult<
  PaginatedResponse<DispensationEvidenceListModel>,
  Error
> =>
  useQuery({
    queryKey: dispensationsQueryKey({ userId, page, pageSize, reported }),
    queryFn: () =>
      getDispensations({
        userId: userId ?? "",
        page,
        pageSize,
        reported,
      }),
    enabled: !!userId,
  });

export const useDispensation = ({
  userId,
  dispensationId,
}: UseDispensationParams): UseQueryResult<DispensationEvidenceModel, Error> =>
  useQuery({
    queryKey: dispensationQueryKey({ userId, dispensationId }),
    queryFn: () =>
      getDispensationById({
        userId: userId ?? "",
        dispensationId: dispensationId ?? "",
      }),
    enabled: !!userId && !!dispensationId,
  });

export const useDownloadDispensation = (): UseMutationResult<
  void,
  Error,
  DownloadDispensationParams
> =>
  useMutation({
    mutationFn: openDispensationDownload,
  });

export const useDispensationPdf = ({
  userId,
  dispensationId,
}: UseDispensationParams): UseQueryResult<Blob, Error> =>
  useQuery({
    queryKey: dispensationPdfQueryKey({ userId, dispensationId }),
    queryFn: () =>
      getDispensationPdfBlob({
        userId: userId ?? "",
        dispensationId: dispensationId ?? "",
      }),
    enabled: !!userId && !!dispensationId,
  });

export const useCreateDispensation = (): UseMutationResult<
  DispensationEvidenceModel,
  Error,
  CreateDispensationParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDispensation,
    onSuccess: (_data, { userId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["cle-api", "users", userId, "dispensations"],
      });
    },
  });
};

export const useUpdateDispensation = (): UseMutationResult<
  DispensationEvidenceModel,
  Error,
  UpdateDispensationParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDispensation,
    onSuccess: (data, { dispensationId, userId }) => {
      queryClient.setQueryData<DispensationEvidenceModel>(
        dispensationQueryKey({ userId, dispensationId }),
        (old) => mergeEvidenceDetailUpdate(old, data),
      );
      void queryClient.invalidateQueries({
        queryKey: reviewThreadAuditTrailQueryKey(dispensationId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["cle-api", "users", userId, "dispensations"],
      });
    },
  });
};
